import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Upload, RefreshCw, Trash2, Download, Plus, Edit, Check, X, LogOut,
  BookOpen, Layers, BarChart2, FileText, Clock, List, PlayCircle, ChevronDown, ChevronUp, AlertCircle, CheckCircle,
  Brain,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem("lumis_admin_token")}` });

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pdfs");
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/me`, { headers: hdrs() }).catch(() => {
      localStorage.removeItem("lumis_admin_token");
      navigate("/admin", { replace: true });
    });
  }, [navigate]);

  // Poll unread count every 30s
  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications/unread-count`, { headers: hdrs() });
      setUnreadCount(r.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchUnread]);

  const handleTabChange = (id) => {
    setTab(id);
    if (id === "relatorios" && unreadCount > 0) {
      axios.post(`${API}/admin/notifications/mark-read`, {}, { headers: hdrs() })
        .then(() => setUnreadCount(0)).catch(() => {});
    }
  };

  const TABS = [
    { id: "pdfs", label: "PDFs", icon: FileText },
    { id: "fila", label: "Fila", icon: List },
    { id: "relatorios", label: "Relatórios", icon: Clock, badge: unreadCount },
    { id: "estante", label: "Estante", icon: BookOpen },
    { id: "modulos", label: "Módulos", icon: Layers },
    { id: "votacao", label: "Votação", icon: BarChart2 },
    { id: "debug", label: "Debug RAG", icon: AlertCircle },
    { id: "ia", label: "IA", icon: Brain },
  ];

  return (
    <div className="min-h-screen bg-[#EEF2FF] flex flex-col" data-testid="admin-dashboard">
      <div className="bg-[#EEF2FF]/95 backdrop-blur-xl border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <span className="text-[#1E3A8A] font-outfit text-xl">✦</span>
          <div>
            <h1 className="text-gray-900 font-outfit font-light text-lg">Lumis Admin</h1>
            <p className="text-gray-500 text-xs font-manrope">Painel de Administração</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={() => { localStorage.removeItem("lumis_admin_token"); navigate("/admin"); }}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm font-manrope transition-all"
        >
          <LogOut size={16} /><span className="hidden sm:inline">Sair</span>
        </button>
      </div>

      <div className="bg-[#EEF2FF] border-b border-gray-200 px-2 flex gap-0 sticky top-[65px] z-10 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => handleTabChange(t.id)}
              className={`relative flex items-center gap-1.5 px-3 py-3.5 text-xs font-manrope border-b-2 transition-all shrink-0 ${
                tab === t.id ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{t.label}</span>
              {t.badge > 0 && (
                <span data-testid={`tab-badge-${t.id}`} className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#1E3A8A] text-white rounded-full text-[9px] flex items-center justify-center font-bold">
                  {t.badge > 9 ? "9+" : t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 p-5 md:p-8 max-w-4xl mx-auto w-full">
        {tab === "pdfs" && <PDFTab />}
        {tab === "fila" && <FilaTab />}
        {tab === "relatorios" && <RelatoriosTab />}
        {tab === "estante" && <EstandeTab />}
        {tab === "modulos" && <ModulosTab />}
        {tab === "votacao" && <VotacaoTab />}
        {tab === "debug" && <DebugRAGTab />}
        {tab === "ia" && <IATab />}
      </div>
    </div>
  );
}

/* ─── TAB: Debug RAG ─── */
function DebugRAGTab() {
  const [traces, setTraces] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rag-traces?limit=50`, { headers: hdrs() });
      setTraces(r.data.traces || []);
      setTotal(r.data.total || 0);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Apagar todos os traces de debug? Esta ação não pode ser desfeita.")) return;
    try {
      await axios.delete(`${API}/admin/rag-traces`, { headers: hdrs() });
      await load();
    } catch {}
  };

  const phaseColor = (phase) => {
    if (phase === "abertura") return "bg-blue-50 text-blue-700 border-blue-200";
    if (phase === "desenvolvimento") return "bg-[#DBEAFE] text-[#1E3A8A] border-[#1E3A8A]/20";
    if (phase === "encerramento") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-gray-100 text-gray-500 border-gray-300";
  };

  return (
    <div className="space-y-5" data-testid="admin-debug-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-gray-900 text-xl font-outfit font-light">Debug do Cérebro (RAG)</h2>
          <p className="text-gray-500 text-sm font-manrope mt-1">
            Auditoria em tempo real: gavetas consultadas, gatilhos casados, fase do ciclo socrático e instrução do turno.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="debug-refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-manrope px-3 py-2 rounded-lg border border-gray-300 transition-all"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
          <button
            data-testid="debug-clear-btn"
            onClick={clearAll}
            className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-manrope px-3 py-2 rounded-lg border border-red-200 transition-all"
          >
            <Trash2 size={14} /> Limpar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <p className="text-gray-500 text-xs font-manrope">Mensagens auditadas</p>
          <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{total}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <p className="text-gray-500 text-xs font-manrope">Mostrando</p>
          <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{traces.length}</p>
        </div>
      </div>

      {traces.length === 0 && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">Nenhum trace ainda. Envie uma mensagem no chat para gerar.</p>
        </div>
      )}

      <div className="space-y-3">
        {traces.map((t) => {
          const isOpen = expanded[t.trace_id];
          const d = t.debug || {};
          return (
            <div
              key={t.trace_id}
              data-testid={`trace-${t.trace_id}`}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                data-testid={`trace-toggle-${t.trace_id}`}
                onClick={() => setExpanded((p) => ({ ...p, [t.trace_id]: !p[t.trace_id] }))}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`text-[10px] font-manrope px-2 py-0.5 rounded-full border ${phaseColor(d.phase)}`}>
                      {d.phase || "?"}
                    </span>
                    <span className="text-[10px] font-manrope text-gray-500">
                      Turno {d.turn} • Ciclo {d.cycle_position}/5
                    </span>
                    <span className="text-[10px] font-manrope text-gray-500">{fmt(t.created_at)}</span>
                  </div>
                  <p className="text-gray-900 text-sm font-manrope truncate">
                    <span className="text-[#1E3A8A]">›</span> {t.user_message}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] font-manrope text-gray-500">
                    <span>📚 {d.gavetas_injected?.length || 0} gaveta(s)</span>
                    <span>🧩 {d.experiences_injected || 0} experiência(s)</span>
                    <span>📝 {d.context_length || 0} chars</span>
                  </div>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </button>

              {isOpen && (
                <div className="border-t border-gray-200 p-4 space-y-4 bg-[#EEF2FF]/50">
                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">Instrução do turno</h4>
                    <p className="text-gray-700 text-xs font-manrope leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-200">
                      {d.cycle_instruction || "—"}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">
                      Gavetas consultadas ({d.gavetas_consulted?.length || 0})
                    </h4>
                    {d.gavetas_consulted?.length > 0 ? (
                      <div className="space-y-1.5">
                        {d.gavetas_consulted.map((g, i) => (
                          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-900 text-xs font-manrope font-medium">{g.gaveta}</span>
                              <span className="text-[10px] font-manrope text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                score {g.score}
                              </span>
                            </div>
                            {g.triggers_matched?.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {g.triggers_matched.map((tr, j) => (
                                  <span key={j} className="text-[10px] font-manrope text-[#1E3A8A] bg-[#DBEAFE] border border-[#1E3A8A]/20 px-1.5 py-0.5 rounded">
                                    {tr}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-xs font-manrope italic">Nenhuma gaveta casou — IA usou conhecimento geral.</p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">Resposta da IA</h4>
                    <p className="text-gray-700 text-xs font-manrope leading-relaxed whitespace-pre-line bg-gray-50 rounded-lg p-3 border border-gray-200 max-h-48 overflow-y-auto">
                      {t.ai_response}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── TAB: PDFs ─── */
function PDFTab() {
  const [livros, setLivros] = useState([]);
  const [processados, setProcessados] = useState([]);
  const [view, setView] = useState("aguardando");
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [cataloging, setCataloging] = useState(null);
  const [jobDone, setJobDone] = useState(false);
  const [lastQueued, setLastQueued] = useState(null);
  const [destino, setDestino] = useState("estante");
  const [tambemPerguntas, setTambemPerguntas] = useState(false);
  const [modules, setModules] = useState([]);
  const [gavetas, setGavetas] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [gavetaChoice, setGavetaChoice] = useState(""); // "" | "__new__" | gaveta_id
  const [novaGavetaNome, setNovaGavetaNome] = useState("");
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const logEnd = useRef(null);

  useEffect(() => { load(); loadModules(); loadGavetas(); return () => clearInterval(pollRef.current); }, []);
  useEffect(() => { logEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [logs]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/livros`, { headers: hdrs() });
      setLivros(r.data.aguardando || r.data.livros || []);
      setProcessados(r.data.processados || []);
    } catch {}
  };

  const loadModules = async () => {
    try {
      const r = await axios.get(`${API}/modules`, { headers: hdrs() });
      setModules((r.data.modules || []).filter(m => (m.categorias || []).length > 0));
    } catch {}
  };

  const loadGavetas = async () => {
    try {
      const r = await axios.get(`${API}/admin/gavetas`, { headers: hdrs() });
      setGavetas(r.data.gavetas || []);
    } catch {}
  };

  const selectedModule = modules.find(m => m.module_id === moduleId);
  const moduleCatsLower = (selectedModule?.categorias || []).map(c => c.toLowerCase());
  const gavetasDoModulo = selectedModule
    ? gavetas.filter(g => moduleCatsLower.includes((g.categoria_principal || "").toLowerCase())
        || (g.categorias_secundarias || []).some(c => moduleCatsLower.includes((c || "").toLowerCase())))
    : [];
  const gavetaSelecionada = gavetas.find(g => g.gaveta_id === gavetaChoice);

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true); setLastQueued(null);
    let queued = 0, failed = 0;
    const categoria = gavetaChoice === "__new__" ? novaGavetaNome.trim() : (gavetaSelecionada?.categoria_principal || "");
    const gavetaNome = gavetaChoice === "__new__" ? novaGavetaNome.trim() : (gavetaSelecionada?.nome_gaveta || "");
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("destino", destino);
      if (moduleId && categoria) {
        fd.append("categoria", categoria);
        fd.append("gaveta_nome", gavetaNome);
        fd.append("module_id", moduleId);
        fd.append("module_name", selectedModule?.name || "");
      }
      if (destino === "estante" && tambemPerguntas) fd.append("also_basedeperguntas", "true");
      try {
        const r = await axios.post(`${API}/admin/upload-pdf`, fd, { headers: { ...hdrs(), "Content-Type": "multipart/form-data" } });
        if (r.data.queued) queued++;
        if (r.data.queued_perguntas) queued++;
      } catch { failed++; }
    }
    if (failed > 0) alert(`${failed} arquivo(s) falharam no upload.`);
    setLastQueued(queued > 0 ? `${queued} arquivo(s) adicionado(s) à fila` : null);
    await load();
    setUploading(false); fileRef.current.value = "";
  };

  const catalog = async (filename) => {
    setCataloging(filename); setLogs([]); setJobDone(false);
    try {
      const r = await axios.post(`${API}/admin/catalog-pdf/${filename}`, {}, { headers: hdrs() });
      const id = r.data.job_id; setJobId(id);
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/catalog-status/${id}`, { headers: hdrs() });
          setLogs(s.data.log || []);
          if (s.data.status === "done" || s.data.status === "error") {
            clearInterval(pollRef.current); setJobDone(true);
            if (s.data.status === "done") { setCataloging(null); await load(); await loadGavetas(); }
          }
        } catch {}
      }, 1500);
    } catch (err) { alert("Erro: " + (err.response?.data?.detail || err.message)); setCataloging(null); }
  };

  const deleteLivro = async (fn) => {
    if (!window.confirm(`Deletar ${fn}?`)) return;
    try { await axios.delete(`${API}/admin/livros/${fn}`, { headers: hdrs() }); await load(); } catch {}
  };

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Livros & Catalogação</h2>
        <input ref={fileRef} type="file" accept=".md" multiple onChange={upload} className="hidden" data-testid="pdf-file-input" />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white border border-gray-300 rounded-full px-4 py-2 text-sm font-manrope">
            {[["estante", "📚 Estante"], ["basedeperguntas", "💬 Base de Perguntas"]].map(([val, label]) => (
              <label key={val} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="radio"
                  name="destino"
                  value={val}
                  checked={destino === val}
                  onChange={() => setDestino(val)}
                  className="accent-[#1E3A8A]"
                  data-testid={`destino-${val}`}
                />
                <span className={destino === val ? "text-gray-900" : "text-gray-500"}>{label}</span>
              </label>
            ))}
          </div>
          {destino === "estante" && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs font-manrope text-gray-500">
              <input
                type="checkbox"
                checked={tambemPerguntas}
                onChange={e => setTambemPerguntas(e.target.checked)}
                className="accent-[#1E3A8A]"
                data-testid="tambem-perguntas-checkbox"
              />
              + Base de Perguntas também
            </label>
          )}
          <button
            data-testid="upload-pdf-btn"
            onClick={() => fileRef.current.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <Upload size={14} />{uploading ? "Enviando..." : "Adicionar Livros (.md)"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-gray-500 text-xs font-manrope">Vincular o próximo upload a um módulo (opcional — evita a IA escolher uma categoria/nome que não bate com o módulo):</span>
        <select
          data-testid="upload-module-select"
          value={moduleId}
          onChange={e => { setModuleId(e.target.value); setGavetaChoice(""); setNovaGavetaNome(""); }}
          className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A]"
        >
          <option value="">Nenhum (categoria e nome automáticos)</option>
          {modules.map(m => <option key={m.module_id} value={m.module_id}>{m.name}</option>)}
        </select>
        {selectedModule && (
          <select
            data-testid="upload-gaveta-select"
            value={gavetaChoice}
            onChange={e => setGavetaChoice(e.target.value)}
            className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A]"
          >
            <option value="">Selecione a gaveta...</option>
            {gavetasDoModulo.map(g => <option key={g.gaveta_id} value={g.gaveta_id}>{g.nome_gaveta}</option>)}
            <option value="__new__">+ Criar nova gaveta</option>
          </select>
        )}
        {gavetaChoice === "__new__" && (
          <input
            data-testid="upload-nova-gaveta-input"
            type="text"
            placeholder="Nome da nova gaveta"
            value={novaGavetaNome}
            onChange={e => setNovaGavetaNome(e.target.value)}
            className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A] placeholder:text-gray-400"
          />
        )}
      </div>

      {lastQueued && (
        <div data-testid="queued-notice" className="bg-[#DBEAFE] border border-[#1E3A8A]/20 rounded-2xl px-4 py-3 flex items-center gap-2 lumis-fade">
          <Clock size={14} className="text-[#1E3A8A] shrink-0" />
          <p className="text-[#1E3A8A] text-sm font-manrope"><span className="font-medium">{lastQueued}</span> adicionado à fila de processamento automático.</p>
        </div>
      )}

      {/* Toggle entre Aguardando / Processados */}
      <div className="flex gap-2 bg-white border border-gray-200 rounded-full p-1 w-fit">
        <button
          data-testid="livros-tab-aguardando"
          onClick={() => setView("aguardando")}
          className={`px-4 py-1.5 rounded-full text-xs font-manrope transition-all ${view === "aguardando" ? "bg-[#1E3A8A] text-white" : "text-gray-500 hover:text-gray-900"}`}
        >
          Aguardando ({livros.length})
        </button>
        <button
          data-testid="livros-tab-processados"
          onClick={() => setView("processados")}
          className={`px-4 py-1.5 rounded-full text-xs font-manrope transition-all ${view === "processados" ? "bg-[#1E3A8A] text-white" : "text-gray-500 hover:text-gray-900"}`}
        >
          Processados ({processados.length})
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">
            {view === "aguardando"
              ? `Em uploadLivros/ — aguardando processamento (${livros.length})`
              : `Em uploadLivros/processados/ — histórico (${processados.length})`}
          </p>
        </div>
        {view === "aguardando" ? (
          livros.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">Adicione um livro em .md para começar.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {livros.map(l => (
                <div key={l.filename} className="px-5 py-4 flex items-center gap-3" data-testid={`livro-row-${l.filename}`}>
                  <span className="text-[#1E3A8A] text-sm shrink-0">{l.type === "md" || l.type === "markdown" ? "📝" : "📄"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{l.filename}</p>
                    <p className="text-gray-500 text-xs font-manrope">{(l.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`catalog-btn-${l.filename}`}
                      onClick={() => catalog(l.filename)}
                      disabled={!!cataloging}
                      className="flex items-center gap-1.5 bg-[#DBEAFE] text-[#1E3A8A] border border-[#1E3A8A]/20 rounded-full px-3 py-1.5 text-xs font-manrope hover:bg-[#1E3A8A]/25 transition-all disabled:opacity-50"
                    >
                      <RefreshCw size={11} className={cataloging === l.filename ? "animate-spin" : ""} />
                      Catalogar
                    </button>
                    <button data-testid={`delete-livro-${l.filename}`} onClick={() => deleteLivro(l.filename)} className="text-gray-500 hover:text-red-400 transition-all p-1.5">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          processados.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">Nenhum arquivo processado ainda.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {processados.map(l => (
                <div key={l.relative_path} className="px-5 py-3 flex items-center gap-3" data-testid={`processado-row-${l.filename}`}>
                  <span className="text-emerald-400 text-sm shrink-0">{l.type === "md" || l.type === "markdown" ? "📝" : "📄"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{l.filename}</p>
                    <p className="text-gray-500 text-xs font-manrope">📅 {l.processed_date} · {(l.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">Catalogado</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {(jobId || logs.length > 0) && (
        <div className="bg-[#0a0a14] border border-[#1E3A8A]/25 rounded-3xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${jobDone ? "bg-green-400" : "bg-[#1E3A8A] animate-pulse"}`} />
            <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Log de Processamento</p>
          </div>
          <div className="p-5 max-h-56 overflow-y-auto space-y-1 font-mono text-xs" data-testid="catalog-log">
            {logs.map((l, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-[#1E3A8A]/40 shrink-0">[{new Date(l.time).toLocaleTimeString("pt-BR")}]</span>
                <span className={l.msg.startsWith("✓") ? "text-green-400" : l.msg.startsWith("✗") ? "text-red-400" : "text-gray-500"}>{l.msg}</span>
              </div>
            ))}
            <div ref={logEnd} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── TAB: FILA ─── */
function FilaTab() {
  const [queue, setQueue] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [processingCount, setProcessingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [schedCfg, setSchedCfg] = useState({ hour: 4, minute: 0, next_run: null, max_retries: 3, retry_interval_seconds: 120 });
  const [editingSched, setEditingSched] = useState(false);
  const [draftHour, setDraftHour] = useState("04");
  const [draftMinute, setDraftMinute] = useState("00");
  const pollRef = useRef(null);

  const loadScheduler = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/scheduler-config`, { headers: hdrs() });
      setSchedCfg(r.data);
      setDraftHour(String(r.data.hour).padStart(2, "0"));
      setDraftMinute(String(r.data.minute).padStart(2, "0"));
    } catch {}
  }, []);

  const saveScheduler = async () => {
    const h = parseInt(draftHour, 10); const m = parseInt(draftMinute, 10);
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      alert("Hora inválida. Use 0-23 e 0-59."); return;
    }
    try {
      await axios.put(`${API}/admin/scheduler-config`, { hour: h, minute: m }, { headers: hdrs() });
      setEditingSched(false);
      await loadScheduler();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/queue`, { headers: hdrs() });
      setQueue(r.data.queue);
      setPendingCount(r.data.pending_count);
      setProcessingCount(r.data.processing_count);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadScheduler();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [load, loadScheduler]);

  const processNow = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/process-now`, {}, { headers: hdrs() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao iniciar processamento");
    } finally { setProcessing(false); }
  };

  const removeItem = async (queueId) => {
    try { await axios.delete(`${API}/admin/queue/${queueId}`, { headers: hdrs() }); await load(); } catch {}
  };

  const cleanOrphans = async () => {
    if (!window.confirm("Remover da fila todos os arquivos perdidos (que não estão mais em disco)?")) return;
    try {
      const r = await axios.post(`${API}/admin/queue/clean-orphans`, {}, { headers: hdrs() });
      alert(`${r.data.removed} item(ns) órfão(s) removido(s) da fila.`);
      await load();
    } catch {}
  };

  const retryItem = async (queueId) => {
    try {
      await axios.post(`${API}/admin/queue/${queueId}/retry`, {}, { headers: hdrs() });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao re-tentar");
    }
  };

  const statusColor = (s) => ({
    pending: "text-yellow-700 bg-yellow-50 border-yellow-200",
    processing: "text-blue-700 bg-blue-50 border-blue-200",
    done: "text-green-700 bg-green-50 border-green-200",
    failed: "text-red-700 bg-red-50 border-red-200"
  }[s] || "text-gray-500 bg-gray-100 border-gray-300");

  const statusLabel = (s) => ({ pending: "Aguardando", processing: "Processando...", done: "Concluído", failed: "Falhou" }[s] || s);

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-900 font-outfit font-light text-xl">Fila de Processamento</h2>
          <p className="text-gray-500 text-xs font-manrope mt-0.5">Sistema 100% automático com 3 tentativas em caso de falha</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="clean-orphans-btn"
            onClick={cleanOrphans}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-full px-3 py-2 text-xs font-manrope hover:bg-gray-200 transition-all"
            title="Remover entradas cujos arquivos não estão mais no disco"
          >
            <Trash2 size={12} /> Limpar órfãos
          </button>
          <button
            data-testid="process-now-btn"
            onClick={processNow}
            disabled={processing || pendingCount === 0 || processingCount > 0}
            className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <PlayCircle size={14} className={processing ? "animate-spin" : ""} />
            {processingCount > 0 ? "Processando..." : "Processar Agora"}
          </button>
        </div>
      </div>

      {/* Card de configuração do agendamento */}
      <div data-testid="scheduler-card" className="bg-white border border-[#1E3A8A]/15 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] shrink-0">
            <Clock size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 text-sm font-manrope">
              Processamento diário às <span className="text-[#1E3A8A] font-medium" data-testid="sched-current-time">{String(schedCfg.hour).padStart(2, "0")}:{String(schedCfg.minute).padStart(2, "0")}</span> <span className="text-gray-500">(Brasília)</span>
            </p>
            <p className="text-gray-500 text-xs font-manrope mt-0.5 truncate">
              {schedCfg.next_run ? `Próxima: ${fmt(schedCfg.next_run)}` : "—"} · 3 tentativas com 2 min de intervalo
            </p>
          </div>
          {!editingSched && (
            <button
              data-testid="edit-scheduler-btn"
              onClick={() => setEditingSched(true)}
              className="flex items-center gap-1.5 text-[#1E3A8A] text-xs font-manrope hover:text-gray-900 transition-all px-3 py-1.5 rounded-full bg-[#DBEAFE] border border-[#1E3A8A]/20 shrink-0"
            >
              ✎ Editar
            </button>
          )}
        </div>
        {editingSched && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
            <input
              type="number" min="0" max="23" value={draftHour}
              onChange={e => setDraftHour(e.target.value)}
              data-testid="sched-hour-input"
              className="w-14 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center text-sm font-manrope py-1.5 outline-none focus:border-[#1E3A8A]"
            />
            <span className="text-gray-500">:</span>
            <input
              type="number" min="0" max="59" value={draftMinute}
              onChange={e => setDraftMinute(e.target.value)}
              data-testid="sched-minute-input"
              className="w-14 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center text-sm font-manrope py-1.5 outline-none focus:border-[#1E3A8A]"
            />
            <button data-testid="sched-save-btn" onClick={saveScheduler} className="bg-[#1E3A8A] text-white text-xs font-manrope rounded-full px-3 py-1.5 hover:bg-[#172554] transition-all">
              Salvar
            </button>
            <button onClick={() => setEditingSched(false)} className="text-gray-500 text-xs font-manrope hover:text-gray-900 transition-all px-2">
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Aguardando", val: pendingCount, color: "text-yellow-600" },
          { label: "Processando", val: processingCount, color: "text-blue-600" },
          { label: "Total na fila", val: queue.length, color: "text-[#1E3A8A]" }
        ].map((c, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
            <p className={`text-2xl font-outfit font-light mt-1 ${c.color}`}>{c.val}</p>
          </div>
        ))}
      </div>

      {processingCount > 0 && (
        <div data-testid="processing-banner" className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-2">
          <RefreshCw size={14} className="text-blue-500 animate-spin shrink-0" />
          <p className="text-blue-700 text-sm font-manrope">Processamento em andamento. Esta tela atualiza automaticamente.</p>
        </div>
      )}

      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> : (
        <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Todos os itens ({queue.length})</p>
          </div>
          {queue.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">
              Fila vazia. Faça upload de um PDF para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {queue.map(item => (
                <div key={item.queue_id} data-testid={`queue-item-${item.queue_id}`} className="px-5 py-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-gray-900 text-sm font-manrope truncate">{item.filename}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${statusColor(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                      {item.gaveta_name && <span className="text-gray-500 text-xs font-manrope">→ {item.gaveta_name}</span>}
                      {item.module_name && <span className="text-[#1E3A8A] text-xs font-manrope" title={`Categoria travada: ${item.forced_categoria}`}>🔒 {item.module_name}</span>}
                      {item.error && <span className="text-red-600 text-xs font-manrope truncate max-w-[200px]" title={item.error}>⚠ {item.error}</span>}
                    </div>
                    <p className="text-gray-500 text-xs font-manrope">
                      Adicionado: {fmt(item.added_at)}
                      {item.processed_at && ` · Processado: ${fmt(item.processed_at)}`}
                    </p>
                  </div>
                  {item.status === "pending" && (
                    <button
                      data-testid={`remove-queue-${item.queue_id}`}
                      onClick={() => removeItem(item.queue_id)}
                      className="text-gray-500 hover:text-red-400 transition-all p-1.5 shrink-0"
                      title="Remover da fila"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {item.status === "failed" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`retry-queue-${item.queue_id}`}
                        onClick={() => retryItem(item.queue_id)}
                        className="text-[#1E3A8A] hover:text-gray-900 transition-all p-1.5"
                        title="Re-tentar processamento"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        data-testid={`remove-queue-${item.queue_id}`}
                        onClick={() => removeItem(item.queue_id)}
                        className="text-gray-500 hover:text-red-400 transition-all p-1.5"
                        title="Remover da fila"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── TAB: RELATÓRIOS ─── */
function RelatoriosTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/admin/reports`, { headers: hdrs() }); setReports(r.data.reports); }
      catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const dur = (s) => {
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  return (
    <div className="space-y-5 lumis-fade">
      <div>
        <h2 className="text-gray-900 font-outfit font-light text-xl">Histórico de Relatórios</h2>
        <p className="text-gray-500 text-xs font-manrope mt-0.5">Gerado automaticamente após cada processamento noturno</p>
      </div>

      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> :
        reports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-3xl px-6 py-12 text-center space-y-2">
            <p className="text-gray-500 text-sm font-manrope">Nenhum relatório ainda.</p>
            <p className="text-gray-500 text-xs font-manrope">Os relatórios são gerados automaticamente após o processamento noturno (04:00h) ou manual.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => {
              const isExp = expanded === r.report_id;
              const hasFailures = r.books_failed?.length > 0;
              return (
                <div key={r.report_id} data-testid={`report-row-${r.report_id}`} className={`bg-white border rounded-3xl overflow-hidden transition-all ${!r.is_read ? "border-[#1E3A8A]/20" : "border-gray-200"}`}>
                  <div
                    className="px-5 py-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-all"
                    onClick={() => setExpanded(isExp ? null : r.report_id)}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${hasFailures ? "bg-yellow-50" : "bg-green-50"}`}>
                      {hasFailures ? <AlertCircle size={15} className="text-yellow-600" /> : <CheckCircle size={15} className="text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-900 text-sm font-outfit font-medium">
                          {fmt(r.started_at)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${r.triggered_by === "manual" ? "text-[#1E3A8A] bg-[#DBEAFE] border-[#1E3A8A]/20" : "text-gray-500 bg-gray-100 border-gray-300"}`}>
                          {r.triggered_by === "manual" ? "Manual" : "Automático"}
                        </span>
                        {!r.is_read && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E3A8A] text-white font-manrope font-medium">Novo</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <p className="text-gray-500 text-xs font-manrope">{r.books_processed?.length || 0} livros · {r.total_insights} insights · {r.total_questions} perguntas · {r.total_triggers} gatilhos</p>
                        <p className="text-gray-500 text-xs font-manrope">{dur(r.duration_seconds)}</p>
                      </div>
                      {hasFailures && <p className="text-yellow-700 text-xs font-manrope mt-1">{r.books_failed.length} falha(s)</p>}
                    </div>
                    {isExp ? <ChevronUp size={16} className="text-gray-500 shrink-0 mt-1" /> : <ChevronDown size={16} className="text-gray-500 shrink-0 mt-1" />}
                  </div>

                  {isExp && (
                    <div className="px-5 pb-5 pt-3 border-t border-gray-200 space-y-4">
                      {/* Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: "Estante antes", val: r.gavetas_count_before },
                          { label: "Estante depois", val: r.gavetas_count_after },
                          { label: "Livros OK", val: r.books_processed?.length || 0 },
                          { label: "Livros c/ falha", val: r.books_failed?.length || 0 }
                        ].map((c, i) => (
                          <div key={i} className="bg-[#EEF2FF] rounded-xl p-3">
                            <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
                            <p className="text-gray-900 text-lg font-outfit font-light">{c.val}</p>
                          </div>
                        ))}
                      </div>

                      {/* Livros processados */}
                      {r.books_processed?.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-2">Livros Processados com Sucesso</p>
                          <div className="space-y-2">
                            {r.books_processed.map((b, i) => (
                              <div key={i} data-testid={`report-book-${i}`} className="bg-[#EEF2FF] rounded-xl px-4 py-3 flex items-start gap-3">
                                <CheckCircle size={14} className="text-green-600 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 text-xs font-manrope font-medium truncate">{b.filename}</p>
                                  <p className="text-gray-500 text-xs font-manrope">{b.gaveta_name}</p>
                                  <p className="text-gray-500 text-xs font-manrope">{b.insights_count} insights · {b.questions_count} perguntas · {b.triggers_count} gatilhos · {dur(b.duration_seconds)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Livros com falha */}
                      {r.books_failed?.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-2">Livros com Falha</p>
                          <div className="space-y-2">
                            {r.books_failed.map((b, i) => (
                              <div key={i} className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
                                <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-gray-900 text-xs font-manrope font-medium">{b.filename}</p>
                                  <p className="text-red-600 text-xs font-manrope">{b.error}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

/* ─── TAB: ESTANTE ─── */
function EstandeTab() {
  const [gavetas, setGavetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/gavetas`, { headers: hdrs() }); setGavetas(r.data.gavetas); }
    catch {} finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Deletar esta gaveta?")) return;
    try { await axios.delete(`${API}/admin/gavetas/${id}`, { headers: hdrs() }); await load(); } catch {}
  };

  return (
    <div className="space-y-4 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Estante de Gavetas</h2>
        <span className="text-gray-500 text-sm font-manrope">{gavetas.length} gavetas</span>
      </div>
      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> :
        gavetas.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-3xl px-6 py-12 text-center text-gray-500 text-sm font-manrope">Nenhuma gaveta ainda.</div>
        ) : (
          <div className="space-y-2">
            {gavetas.map(g => (
              <div key={g.gaveta_id} className="bg-white border border-gray-200 rounded-3xl overflow-hidden" data-testid={`gaveta-row-${g.gaveta_id}`}>
                <div className="px-5 py-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-all" onClick={() => setExpanded(expanded === g.gaveta_id ? null : g.gaveta_id)}>
                  <div className="w-8 h-8 rounded-xl bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] text-sm shrink-0">✦</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-outfit font-medium truncate">{g.nome_gaveta}</p>
                    <p className="text-gray-500 text-xs font-manrope">{g.categoria_principal} · {g.insights_principais?.length || 0} insights · {g.perguntas_poderosas?.length || 0} perguntas</p>
                    {g.arquivo_origem && <p className="text-[#1E3A8A]/60 text-xs font-manrope truncate mt-0.5">📄 {g.arquivo_origem}</p>}
                  </div>
                  <button data-testid={`delete-gaveta-${g.gaveta_id}`} onClick={e => { e.stopPropagation(); del(g.gaveta_id); }} className="text-gray-500 hover:text-red-400 transition-all p-1.5">
                    <Trash2 size={14} />
                  </button>
                </div>
                {expanded === g.gaveta_id && (
                  <div className="px-5 pb-5 pt-3 border-t border-gray-200 space-y-4">
                    {g.instrucao_para_lumis && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Instrução para o Lumis</p>
                        <p className="text-gray-500 text-xs font-manrope leading-relaxed">{g.instrucao_para_lumis}</p>
                      </div>
                    )}
                    {g.perguntas_poderosas?.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Perguntas Poderosas ({g.perguntas_poderosas.length})</p>
                        <div className="space-y-1">{g.perguntas_poderosas.slice(0, 5).map((p, i) => <p key={i} className="text-gray-500 text-xs font-manrope">• {p}</p>)}</div>
                      </div>
                    )}
                    {g.gatilhos_de_busca?.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Gatilhos de Busca</p>
                        <div className="flex flex-wrap gap-1.5">{g.gatilhos_de_busca.map((t, i) => <span key={i} className="bg-[#DBEAFE] text-[#1E3A8A] text-xs font-manrope px-2 py-0.5 rounded-full border border-[#1E3A8A]/15">{t}</span>)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ─── TAB: MÓDULOS ─── */
function ModulosTab() {
  const [modules, setModules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editMod, setEditMod] = useState(null);
  const [newM, setNewM] = useState({ name: "", description: "", icon: "Star", status: "locked", order: 10, categoriasInput: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { const r = await axios.get(`${API}/modules`, { headers: hdrs() }); setModules(r.data.modules); } catch {} };

  const toggle = async (mod) => {
    const ns = mod.status === "active" ? "locked" : "active";
    try { await axios.put(`${API}/modules/${mod.module_id}`, { status: ns }, { headers: hdrs() }); await load(); } catch {}
  };
  const parseCategorias = (str) => (str || "").split(",").map(s => s.trim()).filter(Boolean);
  const addModule = async () => {
    setSaving(true);
    try {
      const { categoriasInput, ...rest } = newM;
      await axios.post(`${API}/modules`, { ...rest, categorias: parseCategorias(categoriasInput) }, { headers: hdrs() });
      setShowAdd(false);
      setNewM({ name: "", description: "", icon: "Star", status: "locked", order: 10, categoriasInput: "" });
      await load();
    }
    catch {} finally { setSaving(false); }
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { categoriasInput, ...rest } = editMod;
      await axios.put(`${API}/modules/${editMod.module_id}`, { ...rest, categorias: parseCategorias(categoriasInput) }, { headers: hdrs() });
      setEditMod(null); await load();
    }
    catch {} finally { setSaving(false); }
  };
  const delMod = async (id) => {
    if (!window.confirm("Deletar este módulo?")) return;
    try { await axios.delete(`${API}/modules/${id}`, { headers: hdrs() }); await load(); } catch {}
  };

  const SC = { active: "text-green-700 bg-green-50 border-green-200", locked: "text-gray-500 bg-gray-100 border-gray-300", development: "text-yellow-700 bg-yellow-50 border-yellow-200" };

  return (
    <div className="space-y-4 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Módulos Temáticos</h2>
        <button data-testid="add-module-btn" onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all">
          <Plus size={14} /> Novo Módulo
        </button>
      </div>
      {showAdd && (
        <div className="bg-white border border-[#1E3A8A]/20 rounded-3xl p-5 space-y-3 lumis-fade">
          <h3 className="text-gray-900 font-outfit text-sm">Adicionar Módulo</h3>
          <input data-testid="new-module-name" value={newM.name} onChange={e => setNewM({ ...newM, name: e.target.value })} placeholder="Nome do módulo" className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none" />
          <textarea data-testid="new-module-desc" value={newM.description} onChange={e => setNewM({ ...newM, description: e.target.value })} placeholder="Descrição" rows={2} className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none resize-none" />
          <div className="flex gap-2">
            <select data-testid="new-module-status" value={newM.status} onChange={e => setNewM({ ...newM, status: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none flex-1">
              <option value="locked">Bloqueado</option><option value="active">Ativo</option><option value="development">Em desenvolvimento</option>
            </select>
            <input type="number" value={newM.order} onChange={e => setNewM({ ...newM, order: parseInt(e.target.value) || 10 })} placeholder="Ordem" className="w-20 bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none" />
          </div>
          <input
            data-testid="new-module-categorias"
            value={newM.categoriasInput}
            onChange={e => setNewM({ ...newM, categoriasInput: e.target.value })}
            placeholder="Categorias da estante (separadas por vírgula, ex: autoconhecimento, ansiedade-e-medo)"
            className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
          <div className="flex gap-2">
            <button data-testid="save-new-module-btn" onClick={addModule} disabled={saving || !newM.name} className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all"><Check size={13} /> Salvar</button>
            <button onClick={() => setShowAdd(false)} className="flex items-center gap-2 bg-gray-100 text-gray-500 rounded-full px-4 py-2 text-sm font-manrope hover:text-gray-900 transition-all"><X size={13} /> Cancelar</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {modules.map(mod => (
          <div key={mod.module_id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5 flex items-center gap-3" data-testid={`module-admin-row-${mod.module_id}`}>
            {editMod?.module_id === mod.module_id ? (
              <div className="flex-1 flex items-center gap-2 flex-wrap">
                <input value={editMod.name} onChange={e => setEditMod({ ...editMod, name: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none flex-1 min-w-24" />
                <input type="number" min="1" value={editMod.order ?? 10} onChange={e => setEditMod({ ...editMod, order: parseInt(e.target.value) || 1 })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none w-16 text-center" title="Posição" data-testid="edit-module-order" />
                <select value={editMod.status} onChange={e => setEditMod({ ...editMod, status: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none">
                  <option value="locked">Bloqueado</option><option value="active">Ativo</option><option value="development">Em dev</option>
                </select>
                <input
                  data-testid="edit-module-categorias"
                  value={editMod.categoriasInput}
                  onChange={e => setEditMod({ ...editMod, categoriasInput: e.target.value })}
                  placeholder="Categorias da estante (vírgula)"
                  className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none w-full"
                />
                <button onClick={saveEdit} disabled={saving} className="text-green-600 hover:text-green-500 p-1 transition-all"><Check size={16} /></button>
                <button onClick={() => setEditMod(null)} className="text-gray-500 hover:text-gray-900 p-1 transition-all"><X size={16} /></button>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="text-gray-900 text-sm font-manrope truncate">{mod.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope shrink-0 ${SC[mod.status] || SC.locked}`}>{mod.status}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`toggle-status-btn-${mod.module_id}`} onClick={() => toggle(mod)} className={`text-xs px-3 py-1.5 rounded-full font-manrope transition-all ${mod.status === "active" ? "bg-gray-100 text-gray-500 hover:text-gray-900" : "bg-[#DBEAFE] text-[#1E3A8A] hover:bg-[#1E3A8A]/20"}`}>
                    {mod.status === "active" ? "Bloquear" : "Ativar"}
                  </button>
                  <button data-testid={`edit-module-btn-${mod.module_id}`} onClick={() => setEditMod({ ...mod, categoriasInput: (mod.categorias || []).join(", ") })} className="text-gray-500 hover:text-gray-900 transition-all p-1.5"><Edit size={13} /></button>
                  <button data-testid={`delete-module-btn-${mod.module_id}`} onClick={() => delMod(mod.module_id)} className="text-gray-500 hover:text-red-400 transition-all p-1.5"><Trash2 size={13} /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── TAB: VOTAÇÃO ─── */
function VotacaoTab() {
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); try { const r = await axios.get(`${API}/votes`); setVotes(r.data.votes); } catch {} finally { setLoading(false); } };

  const exportCSV = async () => {
    try {
      const r = await axios.get(`${API}/admin/votes-export`, { headers: hdrs(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "lumis-votos.csv"; a.click();
    } catch {}
  };

  const locked = votes.filter(v => v.status !== "active");
  const chartData = locked.map(v => ({ name: v.name.split(" ")[0], votos: v.vote_count }));
  const total = votes.reduce((a, v) => a + v.vote_count, 0);

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Painel de Votação</h2>
        <button data-testid="export-votes-btn" onClick={exportCSV} className="flex items-center gap-2 bg-white text-gray-500 border border-gray-300 rounded-full px-4 py-2.5 text-sm font-manrope hover:text-gray-900 transition-all active:scale-[0.98]">
          <Download size={14} /> Exportar CSV
        </button>
      </div>
      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: "Em votação", val: locked.length },
              { label: "Total de votos", val: total },
              { label: "Módulo líder", val: votes.sort((a, b) => b.vote_count - a.vote_count)[0]?.name?.split(" ")[0] || "—" }
            ].map((c, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
                <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
                <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{c.val}</p>
              </div>
            ))}
          </div>
          {chartData.length > 0 && chartData.some(d => d.votos > 0) && (
            <div className="bg-white border border-gray-200 rounded-3xl p-5">
              <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-4">Ranking Visual</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -25 }}>
                  <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(30,58,138,0.15)", borderRadius: 12, color: "#111827", fontFamily: "Manrope", fontSize: 12 }} cursor={{ fill: "rgba(30,58,138,0.08)" }} />
                  <Bar dataKey="votos" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200">
              <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Ranking Detalhado</p>
            </div>
            <div className="divide-y divide-gray-200">
              {[...votes].sort((a, b) => b.vote_count - a.vote_count).map((v, i) => (
                <div key={v.module_id} className="px-5 py-3.5 flex items-center gap-3" data-testid={`vote-row-${v.module_id}`}>
                  <span className={`text-sm font-outfit font-light w-5 shrink-0 ${i === 0 ? "text-[#1E3A8A]" : "text-gray-400"}`}>#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{v.name}</p>
                    <p className={`text-xs font-manrope ${v.status === "active" ? "text-green-600" : "text-gray-500"}`}>
                      {v.status === "active" ? "Ativo" : `${v.vote_count} votos`}
                    </p>
                  </div>
                  <p className="text-[#1E3A8A] text-sm font-outfit shrink-0">{v.vote_count}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── TAB: IA (Fase 8 — migração de IA) ─── */
function IATab() {
  const [sub, setSub] = useState("flags");
  const SUBS = [
    { id: "flags", label: "Feature Flags" },
    { id: "skills", label: "Skills" },
    { id: "playbooks", label: "Playbooks" },
    { id: "shadow", label: "Shadow Runs" },
    { id: "preview", label: "Preview Humanização" },
    { id: "casos", label: "Casos Reais" },
    { id: "shadow-hum", label: "Shadow Humanização" },
  ];

  return (
    <div className="space-y-4 lumis-fade" data-testid="admin-ia-tab">
      <div>
        <h2 className="text-gray-900 font-outfit font-light text-xl">Migração de IA</h2>
        <p className="text-gray-500 text-sm font-manrope mt-1">
          Pipeline V2 (Kernel/Think/Skills/Critic) — configuração dinâmica, skills, playbooks e comparação shadow com o V1.
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {SUBS.map(s => (
          <button
            key={s.id}
            data-testid={`ia-subtab-${s.id}`}
            onClick={() => setSub(s.id)}
            className={`px-4 py-2 rounded-full text-sm font-manrope border transition-all ${
              sub === s.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-gray-500 border-gray-300 hover:text-gray-900"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "flags" && <AIFlagsSection />}
      {sub === "skills" && <AISkillsSection />}
      {sub === "playbooks" && <AIPlaybooksSection />}
      {sub === "shadow" && <AIShadowRunsSection />}
      {sub === "preview" && <HumanizationPreviewSection />}
      {sub === "casos" && <CasePatternsSection />}
      {sub === "shadow-hum" && <HumanizationShadowRunsSection />}
    </div>
  );
}

function AIFlagsSection() {
  const [form, setForm] = useState(null);
  const [modulesInput, setModulesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai-config`, { headers: hdrs() });
      setForm(r.data.effective);
      setModulesInput((r.data.effective.enabled_modules || []).join(", "));
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pipeline_v2_enabled: form.enabled,
        shadow_mode: form.shadow,
        enabled_modules: modulesInput.split(",").map(s => s.trim()).filter(Boolean),
        critic_enabled: form.critic_enabled,
        supervisor_enabled: form.supervisor_enabled,
        max_revisions: form.max_revisions,
      };
      const r = await axios.put(`${API}/admin/ai-config`, payload, { headers: hdrs() });
      setForm(r.data.effective);
      setModulesInput((r.data.effective.enabled_modules || []).join(", "));
    } catch {} finally { setSaving(false); }
  };

  if (loading || !form) return <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-3xl p-5 space-y-4" data-testid="ia-flags-section">
      <div className="bg-[#EEF2FF] border border-[#1E3A8A]/20 rounded-xl p-3">
        <p className="text-gray-700 text-xs font-manrope leading-relaxed">
          <strong>Pipeline V2 habilitado</strong> só liga o pipeline de verdade se o arquivo <code>.env</code> do servidor
          (<code>AI_PIPELINE_V2_ENABLED</code>) <strong>e</strong> este toggle estiverem ligados ao mesmo tempo — qualquer
          um dos dois desligado já desativa o V2. Os demais campos abaixo têm efeito imediato, sem reiniciar o servidor.
        </p>
      </div>

      <label className="flex items-center justify-between gap-3">
        <span className="text-gray-900 text-sm font-manrope">Pipeline V2 habilitado</span>
        <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} data-testid="ia-flag-enabled" />
      </label>
      <label className="flex items-center justify-between gap-3">
        <span className="text-gray-900 text-sm font-manrope">Shadow mode</span>
        <input type="checkbox" checked={!!form.shadow} onChange={e => setForm({ ...form, shadow: e.target.checked })} data-testid="ia-flag-shadow" />
      </label>
      <label className="flex items-center justify-between gap-3">
        <span className="text-gray-900 text-sm font-manrope">Critic habilitado</span>
        <input type="checkbox" checked={!!form.critic_enabled} onChange={e => setForm({ ...form, critic_enabled: e.target.checked })} data-testid="ia-flag-critic" />
      </label>
      <label className="flex items-center justify-between gap-3">
        <span className="text-gray-900 text-sm font-manrope">Supervisor habilitado</span>
        <input type="checkbox" checked={!!form.supervisor_enabled} onChange={e => setForm({ ...form, supervisor_enabled: e.target.checked })} data-testid="ia-flag-supervisor" />
      </label>
      <div>
        <label className="text-gray-900 text-sm font-manrope block mb-1.5">Máximo de revisões (Fusion)</label>
        <input
          type="number" min="0" value={form.max_revisions}
          onChange={e => setForm({ ...form, max_revisions: parseInt(e.target.value) || 0 })}
          data-testid="ia-flag-max-revisions"
          className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none w-24"
        />
      </div>
      <div>
        <label className="text-gray-900 text-sm font-manrope block mb-1.5">Módulos ativos (V2 real + shadow), separados por vírgula</label>
        <input
          value={modulesInput} onChange={e => setModulesInput(e.target.value)}
          placeholder="ex: Relacionamento Amoroso"
          data-testid="ia-flag-modules"
          className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        />
      </div>

      <button onClick={save} disabled={saving} data-testid="ia-flags-save-btn" className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all">
        <Check size={14} /> Salvar
      </button>
    </div>
  );
}

const HUMANIZATION_MODES = ["acolhedor", "investigativo", "reflexivo", "confirmatorio", "seguro", "pratico"];
const HUMANIZATION_RESPONSE_LENGTHS = ["curta", "media", "longa"];
const HUMANIZATION_SLIDERS = [
  ["warmth", "Calor"], ["directness", "Diretividade"], ["emotional_depth", "Profundidade emocional"], ["formality", "Formalidade"],
];
const HUMANIZATION_CHECKS = [
  ["ask_question", "Fazer pergunta"], ["acknowledge_specific_detail", "Reconhecer detalhe específico"], ["avoid_advice", "Evitar conselho"],
  ["use_literal_example", "Usar exemplo literal"], ["include_next_step", "Incluir próximo passo"], ["use_user_name", "Usar nome do usuário"],
];
const HUMANIZATION_TEXT_LISTS = [
  ["humanization_rules", "Regras de humanização"], ["anti_patterns", "Anti-padrões"],
  ["positive_examples", "Exemplos positivos"], ["negative_examples", "Exemplos negativos"],
];

const _defaultCommunication = () => ({
  mode: "acolhedor", warmth: 0.5, directness: 0.5, emotional_depth: 0.5, formality: 0.5,
  response_length: "media", ask_question: true, acknowledge_specific_detail: true, avoid_advice: true,
  use_literal_example: false, include_next_step: false, use_user_name: false, max_questions: 1, allowed_emojis: 0,
});

function SkillHumanizationEditor({ skill, onSaved }) {
  const c = { ..._defaultCommunication(), ...(skill.communication || {}) };
  const [form, setForm] = useState({
    communication: c,
    humanization_rules: (skill.humanization_rules || []).join("\n"),
    anti_patterns: (skill.anti_patterns || []).join("\n"),
    positive_examples: (skill.positive_examples || []).join("\n"),
    negative_examples: (skill.negative_examples || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);

  const setC = (field, value) => setForm(f => ({ ...f, communication: { ...f.communication, [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        communication: form.communication,
        humanization_rules: form.humanization_rules.split("\n").map(s => s.trim()).filter(Boolean),
        anti_patterns: form.anti_patterns.split("\n").map(s => s.trim()).filter(Boolean),
        positive_examples: form.positive_examples.split("\n").map(s => s.trim()).filter(Boolean),
        negative_examples: form.negative_examples.split("\n").map(s => s.trim()).filter(Boolean),
      };
      await axios.put(`${API}/admin/ai-skills/${skill._id}/humanization`, payload, { headers: hdrs() });
      await onSaved();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="border-t border-gray-200 p-4 space-y-4 bg-[#EEF2FF]/50" data-testid={`ia-skill-humanization-${skill._id}`}>
      <div>
        <label className="text-gray-900 text-xs font-manrope block mb-1.5">Modo</label>
        <select
          value={form.communication.mode} onChange={e => setC("mode", e.target.value)}
          data-testid={`ia-skill-hum-mode-${skill._id}`}
          className="bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        >
          {HUMANIZATION_MODES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {HUMANIZATION_SLIDERS.map(([field, label]) => (
        <div key={field}>
          <label className="text-gray-900 text-xs font-manrope block mb-1">{label}: {form.communication[field]}</label>
          <input
            type="range" min="0" max="1" step="0.1" value={form.communication[field]}
            onChange={e => setC(field, parseFloat(e.target.value))}
            data-testid={`ia-skill-hum-${field}-${skill._id}`}
            className="w-full"
          />
        </div>
      ))}

      <div>
        <label className="text-gray-900 text-xs font-manrope block mb-1.5">Tamanho da resposta</label>
        <select
          value={form.communication.response_length} onChange={e => setC("response_length", e.target.value)}
          data-testid={`ia-skill-hum-response-length-${skill._id}`}
          className="bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        >
          {HUMANIZATION_RESPONSE_LENGTHS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {HUMANIZATION_CHECKS.map(([field, label]) => (
          <label key={field} className="flex items-center gap-2">
            <input
              type="checkbox" checked={!!form.communication[field]} onChange={e => setC(field, e.target.checked)}
              data-testid={`ia-skill-hum-${field}-${skill._id}`}
            />
            <span className="text-gray-700 text-xs font-manrope">{label}</span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-gray-900 text-xs font-manrope block mb-1">Máximo de perguntas</label>
          <input
            type="number" min="0" value={form.communication.max_questions}
            onChange={e => setC("max_questions", parseInt(e.target.value) || 0)}
            data-testid={`ia-skill-hum-max-questions-${skill._id}`}
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
        </div>
        <div>
          <label className="text-gray-900 text-xs font-manrope block mb-1">Emojis permitidos</label>
          <input
            type="number" min="0" value={form.communication.allowed_emojis}
            onChange={e => setC("allowed_emojis", parseInt(e.target.value) || 0)}
            data-testid={`ia-skill-hum-allowed-emojis-${skill._id}`}
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
        </div>
      </div>

      {HUMANIZATION_TEXT_LISTS.map(([field, label]) => (
        <div key={field}>
          <label className="text-gray-900 text-xs font-manrope block mb-1">{label} (um por linha)</label>
          <textarea
            value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
            rows={3}
            data-testid={`ia-skill-hum-${field}-${skill._id}`}
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-xs font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
        </div>
      ))}

      <button onClick={save} disabled={saving} data-testid={`ia-skill-hum-save-${skill._id}`} className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all">
        <Check size={14} /> Salvar humanização (v{(skill.version || 1) + 1})
      </button>
    </div>
  );
}

function AISkillsSection() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPriority, setEditingPriority] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai-skills`, { headers: hdrs() });
      setSkills(r.data.skills || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (skill) => {
    const ns = skill.status === "active" ? "inactive" : "active";
    try { await axios.put(`${API}/admin/ai-skills/${skill._id}`, { status: ns }, { headers: hdrs() }); await load(); } catch {}
  };
  const savePriority = async (skill) => {
    const value = editingPriority[skill._id];
    if (value === undefined) return;
    try {
      await axios.put(`${API}/admin/ai-skills/${skill._id}`, { priority: parseInt(value) || 0 }, { headers: hdrs() });
      setEditingPriority(p => { const n = { ...p }; delete n[skill._id]; return n; });
      await load();
    } catch {}
  };

  if (loading) return <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div>;

  return (
    <div className="space-y-2" data-testid="ia-skills-section">
      {skills.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">Nenhuma skill semeada ainda (ver scripts/seed_ai_v2.py).</p>
        </div>
      )}
      {skills.map(s => {
        const isOpen = expanded[s._id];
        return (
        <div key={s._id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid={`ia-skill-row-${s._id}`}>
          <div className="px-5 py-3.5 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-gray-900 text-sm font-manrope">{s.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${s.status === "active" ? "text-green-700 bg-green-50 border-green-200" : "text-gray-500 bg-gray-100 border-gray-300"}`}>
                  {s.status}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full border font-manrope text-gray-500 bg-gray-100 border-gray-300">v{s.version || 1}</span>
              </div>
              <p className="text-gray-500 text-xs font-manrope">
                módulos: {(s.modules || []).join(", ")} • fases: {(s.phases || []).join(", ")}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                value={editingPriority[s._id] ?? s.priority}
                onChange={e => setEditingPriority({ ...editingPriority, [s._id]: e.target.value })}
                data-testid={`ia-skill-priority-${s._id}`}
                className="w-16 bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-manrope text-center focus:border-[#1E3A8A]/60 outline-none"
              />
              <button onClick={() => savePriority(s)} className="text-green-600 hover:text-green-500 p-1 transition-all" data-testid={`ia-skill-save-priority-${s._id}`}>
                <Check size={16} />
              </button>
              <button
                onClick={() => toggleStatus(s)}
                data-testid={`ia-skill-toggle-${s._id}`}
                className={`text-xs px-3 py-1.5 rounded-full font-manrope transition-all ${s.status === "active" ? "bg-gray-100 text-gray-500 hover:text-gray-900" : "bg-[#DBEAFE] text-[#1E3A8A] hover:bg-[#1E3A8A]/20"}`}
              >
                {s.status === "active" ? "Desativar" : "Ativar"}
              </button>
              <button
                onClick={() => setExpanded(p => ({ ...p, [s._id]: !p[s._id] }))}
                data-testid={`ia-skill-hum-toggle-${s._id}`}
                className="text-xs px-3 py-1.5 rounded-full font-manrope bg-gray-100 text-gray-500 hover:text-gray-900 transition-all flex items-center gap-1"
              >
                Editar humanização {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
          {isOpen && <SkillHumanizationEditor skill={s} onSaved={load} />}
        </div>
        );
      })}
    </div>
  );
}

function AIPlaybooksSection() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/ai-playbooks`, { headers: hdrs() });
        setPlaybooks(r.data.playbooks || []);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div>;

  return (
    <div className="space-y-2" data-testid="ia-playbooks-section">
      {playbooks.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">Nenhum playbook semeado ainda.</p>
        </div>
      )}
      {playbooks.map(p => (
        <div key={p._id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5" data-testid={`ia-playbook-row-${p._id}`}>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-gray-900 text-sm font-manrope">{p.name}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${p.status === "active" ? "text-green-700 bg-green-50 border-green-200" : "text-gray-500 bg-gray-100 border-gray-300"}`}>
              {p.status}
            </span>
          </div>
          <p className="text-gray-500 text-xs font-manrope">
            entrada: {p.entry_phase} • {(p.steps || []).length} passo(s) • módulos: {(p.modules || []).join(", ")}
          </p>
        </div>
      ))}
    </div>
  );
}

function AIShadowRunsSection() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/modules`, { headers: hdrs() }).then(r => setModules(r.data.modules || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (moduleFilter) params.module_id = moduleFilter;
      const r = await axios.get(`${API}/admin/ai-shadow-runs`, { headers: hdrs(), params });
      setRuns(r.data.shadow_runs || []);
      setTotal(r.data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [moduleFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="ia-shadow-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select
          value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}
          data-testid="ia-shadow-module-filter"
          className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        >
          <option value="">Todos os módulos</option>
          {modules.map(m => <option key={m.module_id} value={m.module_id}>{m.name}</option>)}
        </select>
        <button
          data-testid="ia-shadow-refresh-btn"
          onClick={load} disabled={loading}
          className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-manrope px-3 py-2 rounded-lg border border-gray-300 transition-all"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 inline-block">
        <p className="text-gray-500 text-xs font-manrope">Total de shadow runs</p>
        <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{total}</p>
      </div>

      {runs.length === 0 && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">
            Nenhum shadow run ainda — ligue "Shadow mode" e liste um módulo em "Módulos ativos" na aba Feature Flags pra começar a observar.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {runs.map(r => {
          const isOpen = expanded[r.shadow_id];
          const dv = r.divergence || {};
          const v2 = r.v2 || {};
          return (
            <div key={r.shadow_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`ia-shadow-row-${r.shadow_id}`}>
              <button
                onClick={() => setExpanded(p => ({ ...p, [r.shadow_id]: !p[r.shadow_id] }))}
                data-testid={`ia-shadow-toggle-${r.shadow_id}`}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-gray-100 text-gray-500 border-gray-300">{r.phase || "?"}</span>
                    {r.error ? (
                      <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">erro</span>
                    ) : (
                      <>
                        {dv.v2_flagged_risk && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">risco: {v2.risk_level}</span>
                        )}
                        {dv.v2_would_use_safe_response && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200">resposta_segura</span>
                        )}
                        {!dv.text_identical && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-[#DBEAFE] text-[#1E3A8A] border-[#1E3A8A]/20">texto divergente</span>
                        )}
                      </>
                    )}
                    <span className="text-[10px] font-manrope text-gray-500">{fmt(r.created_at)}</span>
                  </div>
                  <p className="text-gray-900 text-sm font-manrope">{r.module_name}</p>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </button>
              {isOpen && (
                <div className="border-t border-gray-200 p-4 space-y-2 bg-[#EEF2FF]/50 text-xs font-manrope text-gray-700">
                  {r.error ? (
                    <p className="text-red-700">{r.error}</p>
                  ) : (
                    <>
                      <p><strong>Intenção:</strong> {v2.intent} • <strong>Tópico:</strong> {v2.topic}</p>
                      <p><strong>Ação (V2):</strong> {v2.action} • <strong>Playbook:</strong> {v2.playbook_id}</p>
                      <p><strong>Skills selecionadas:</strong> {(v2.skills_selected || []).join(", ") || "—"}</p>
                      <p><strong>Gavetas (V2):</strong> {(v2.gavetas_injected || []).join(", ") || "—"}</p>
                      <p><strong>Quality aprovada:</strong> {String(v2.quality_approved)} • <strong>Violações:</strong> {(v2.supervisor_violations || []).join(", ") || "—"}</p>
                      <p><strong>Tokens:</strong> {v2.input_tokens ?? "—"} in / {v2.output_tokens ?? "—"} out • <strong>Latência:</strong> {v2.latency_ms ?? "—"}ms (total {v2.wall_latency_ms ?? "—"}ms)</p>
                      <p><strong>Divergência de tamanho:</strong> V1 {r.v1?.response_length ?? "—"} vs V2 {v2.response_length ?? "—"} (diff {dv.text_length_diff ?? "—"})</p>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HUMANIZATION_PHASES = ["acolhimento", "aprofundamento", "sintese", "confirmacao", "permissao", "caminho", "refinamento", "encerramento"];

const HUMANIZATION_REPORT_SCORES = [
  ["specificity_score", "Especificidade"], ["continuity_score", "Continuidade"], ["empathy_score", "Empatia"],
  ["naturalness_score", "Naturalidade"], ["clarity_score", "Clareza"], ["repetition_score", "Não repetição"],
];

function HumanizationPreviewSection() {
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState("acolhimento");
  const [moduleName, setModuleName] = useState("Relacionamento Amoroso");
  const [compareWithV1, setCompareWithV1] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    if (!message.trim()) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/humanization/preview`, {
        message, phase, module_name: moduleName, history: [], compare_with_v1: compareWithV1,
      }, { headers: hdrs() });
      setResult(r.data);
    } catch {
      setError("Falha ao gerar preview.");
    } finally { setLoading(false); }
  };

  const h = result?.humanized;
  const report = h?.humanization_report;

  return (
    <div className="space-y-4" data-testid="ia-preview-section">
      <div className="bg-white border border-gray-200 rounded-3xl p-5 space-y-4">
        <div>
          <label className="text-gray-900 text-sm font-manrope block mb-1.5">Mensagem do usuário</label>
          <textarea
            value={message} onChange={e => setMessage(e.target.value)} rows={3}
            data-testid="ia-preview-message"
            placeholder="Ex: ele demorou pra responder e eu já fiquei com ciúmes de novo"
            className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
        </div>
        <div className="flex gap-3 flex-wrap">
          <div>
            <label className="text-gray-900 text-sm font-manrope block mb-1.5">Fase</label>
            <select
              value={phase} onChange={e => setPhase(e.target.value)}
              data-testid="ia-preview-phase"
              className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
            >
              {HUMANIZATION_PHASES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-gray-900 text-sm font-manrope block mb-1.5">Módulo</label>
            <input
              value={moduleName} onChange={e => setModuleName(e.target.value)}
              data-testid="ia-preview-module"
              className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
            />
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={compareWithV1} onChange={e => setCompareWithV1(e.target.checked)} data-testid="ia-preview-compare-v1" />
          <span className="text-gray-900 text-sm font-manrope">Comparar com V1</span>
        </label>
        <button
          onClick={run} disabled={loading || !message.trim()} data-testid="ia-preview-run-btn"
          className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all"
        >
          <PlayCircle size={14} /> {loading ? "Gerando..." : "Gerar preview"}
        </button>
        {error && <p className="text-red-700 text-xs font-manrope">{error}</p>}
      </div>

      {h && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="ia-preview-humanized-result">
            <p className="text-gray-900 text-xs font-manrope font-semibold">Resposta humanizada</p>
            <p className="text-gray-700 text-sm font-manrope whitespace-pre-wrap">{h.text}</p>
            {h.communication_profile && (
              <p className="text-gray-500 text-xs font-manrope">
                perfil: {h.communication_profile.mode} • calor {h.communication_profile.warmth} • diretividade {h.communication_profile.directness}
              </p>
            )}
            <button
              onClick={() => setShowInstruction(v => !v)} data-testid="ia-preview-instruction-toggle"
              className="text-[#1E3A8A] text-xs font-manrope flex items-center gap-1"
            >
              Instrução composta {showInstruction ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {showInstruction && (
              <pre className="bg-[#EEF2FF] rounded-lg p-3 text-[11px] font-manrope text-gray-700 whitespace-pre-wrap">{h.composed_instruction}</pre>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="ia-preview-report">
            <div className="flex items-center gap-2">
              <p className="text-gray-900 text-xs font-manrope font-semibold">Avaliação de naturalidade</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-manrope ${report?.approved ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"}`}>
                {report?.approved ? "aprovado" : "reprovado"}
              </span>
            </div>
            {HUMANIZATION_REPORT_SCORES.map(([field, label]) => (
              <div key={field} className="flex items-center justify-between gap-2">
                <span className="text-gray-700 text-xs font-manrope">{label}</span>
                <span className="text-gray-900 text-xs font-manrope">{report?.[field] ?? "—"}</span>
              </div>
            ))}
            {(report?.violations || []).length > 0 && (
              <div>
                <p className="text-gray-900 text-xs font-manrope mb-1">Violações</p>
                <ul className="list-disc list-inside text-red-700 text-xs font-manrope space-y-0.5">
                  {report.violations.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
            {(h.heuristic_violations || []).length > 0 && (
              <div>
                <p className="text-gray-900 text-xs font-manrope mb-1">Violações heurísticas</p>
                <ul className="list-disc list-inside text-yellow-700 text-xs font-manrope space-y-0.5">
                  {h.heuristic_violations.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
          </div>

          {result.v1 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 md:col-span-2" data-testid="ia-preview-v1-result">
              <p className="text-gray-900 text-xs font-manrope font-semibold mb-2">Resposta V1 (comparação)</p>
              <p className="text-gray-700 text-sm font-manrope whitespace-pre-wrap">{result.v1.text}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CasePatternsSection() {
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [themeFilter, setThemeFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { review_status: "pending", limit: 50 };
      if (themeFilter) params.theme = themeFilter;
      const [statsR, patternsR] = await Promise.all([
        axios.get(`${API}/admin/humanization/case-patterns/stats`, { headers: hdrs() }),
        axios.get(`${API}/admin/humanization/case-patterns`, { headers: hdrs(), params }),
      ]);
      setStats(statsR.data);
      setPatterns(patternsR.data.patterns || []);
    } catch {} finally { setLoading(false); }
  }, [themeFilter]);
  useEffect(() => { load(); }, [load]);

  const review = async (caseId, reviewStatus) => {
    try {
      await axios.put(`${API}/admin/humanization/case-patterns/${caseId}`, { review_status: reviewStatus }, { headers: hdrs() });
      await load();
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="ia-case-patterns-section">
      {stats && (
        <div className="flex gap-3 flex-wrap">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs font-manrope">Pendentes</p>
            <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{stats.by_status?.pending ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs font-manrope">Aprovados</p>
            <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{stats.by_status?.approved ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs font-manrope">Rejeitados</p>
            <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{stats.by_status?.rejected ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs font-manrope">Sensíveis</p>
            <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{stats.sensitive_count ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs font-manrope">Total</p>
            <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{stats.total ?? 0}</p>
          </div>
        </div>
      )}

      {stats?.top_themes?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-gray-500 text-xs font-manrope mb-2">Top temas</p>
          <div className="flex gap-2 flex-wrap">
            {stats.top_themes.map(t => (
              <span key={t.theme} className="text-[11px] font-manrope px-2 py-1 rounded-full border bg-gray-100 text-gray-700 border-gray-300">
                {t.theme} ({t.count})
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          value={themeFilter} onChange={e => setThemeFilter(e.target.value)}
          placeholder="Filtrar por tema..."
          data-testid="ia-case-patterns-theme-filter"
          className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        />
        <button
          onClick={load} disabled={loading} data-testid="ia-case-patterns-refresh-btn"
          className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-manrope px-3 py-2 rounded-lg border border-gray-300 transition-all"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      {patterns.length === 0 && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">Nenhum padrão pendente de revisão.</p>
        </div>
      )}

      <div className="space-y-2">
        {patterns.map(p => (
          <div key={p._id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5" data-testid={`ia-case-pattern-row-${p._id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-xs px-2 py-0.5 rounded-full border font-manrope text-gray-500 bg-gray-100 border-gray-300">{p.theme || "sem_categoria"}</span>
              {p.requires_safety_review && (
                <span className="text-xs px-2 py-0.5 rounded-full border font-manrope text-red-700 bg-red-50 border-red-200">revisão de segurança</span>
              )}
            </div>
            {(p.emotions || []).length > 0 && (
              <p className="text-gray-500 text-xs font-manrope mb-1">emoções: {p.emotions.join(", ")}</p>
            )}
            {(p.distinctions || []).length > 0 && (
              <p className="text-gray-700 text-sm font-manrope mb-1">{p.distinctions.join(" • ")}</p>
            )}
            {(p.useful_questions || []).length > 0 && (
              <p className="text-gray-500 text-xs font-manrope mb-2">perguntas: {p.useful_questions.join(" / ")}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => review(p._id, "approved")} data-testid={`ia-case-pattern-approve-${p._id}`}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-manrope bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-all"
              >
                <Check size={12} /> Aprovar
              </button>
              <button
                onClick={() => review(p._id, "rejected")} data-testid={`ia-case-pattern-reject-${p._id}`}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-manrope bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-all"
              >
                <X size={12} /> Rejeitar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HumanizationShadowRunsSection() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/modules`, { headers: hdrs() }).then(r => setModules(r.data.modules || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (moduleFilter) params.module_id = moduleFilter;
      const r = await axios.get(`${API}/admin/humanization/shadow-runs`, { headers: hdrs(), params });
      setRuns(r.data.shadow_runs || []);
      setTotal(r.data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [moduleFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="ia-humanization-shadow-section">
      <div className="bg-[#EEF2FF] border border-[#1E3A8A]/20 rounded-xl p-3">
        <p className="text-gray-700 text-xs font-manrope leading-relaxed">
          Compara, por amostragem (<code>AI_HUMANIZATION_SHADOW_SAMPLE_RATE</code>), a resposta já servida (pipeline V2 sem
          humanização) com a versão humanizada gerada só internamente — nunca afeta o que o usuário recebeu. Use pra decidir
          se vale a pena adicionar um módulo em <code>AI_HUMANIZATION_MODULES</code>, e depois "revisar falhas" aqui e "ajustar
          presets" na aba Skills.
        </p>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select
          value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}
          data-testid="ia-hum-shadow-module-filter"
          className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        >
          <option value="">Todos os módulos</option>
          {modules.map(m => <option key={m.module_id} value={m.module_id}>{m.name}</option>)}
        </select>
        <button
          data-testid="ia-hum-shadow-refresh-btn"
          onClick={load} disabled={loading}
          className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-manrope px-3 py-2 rounded-lg border border-gray-300 transition-all"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 inline-block">
        <p className="text-gray-500 text-xs font-manrope">Total de shadow runs</p>
        <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{total}</p>
      </div>

      {runs.length === 0 && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">
            Nenhum shadow run de humanização ainda — ligue "Shadow" e liste um módulo em "Módulos ativos" (pipeline V2) que
            ainda não esteja em <code>AI_HUMANIZATION_MODULES</code> pra começar a observar.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {runs.map(r => {
          const isOpen = expanded[r.shadow_id];
          const dv = r.divergence || {};
          const h = r.humanized || {};
          const s = r.served || {};
          return (
            <div key={r.shadow_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`ia-hum-shadow-row-${r.shadow_id}`}>
              <button
                onClick={() => setExpanded(p => ({ ...p, [r.shadow_id]: !p[r.shadow_id] }))}
                data-testid={`ia-hum-shadow-toggle-${r.shadow_id}`}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-gray-100 text-gray-500 border-gray-300">{r.phase || "?"}</span>
                    {r.error ? (
                      <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">erro</span>
                    ) : (
                      <>
                        {h.approved === false && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">reprovada</span>
                        )}
                        {dv.safety_violations_diverge && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200">segurança diverge</span>
                        )}
                        {!dv.text_identical && (
                          <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full border bg-[#DBEAFE] text-[#1E3A8A] border-[#1E3A8A]/20">texto divergente</span>
                        )}
                      </>
                    )}
                    <span className="text-[10px] font-manrope text-gray-500">{fmt(r.created_at)}</span>
                  </div>
                  <p className="text-gray-900 text-sm font-manrope">{r.module_name}</p>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </button>
              {isOpen && (
                <div className="border-t border-gray-200 p-4 space-y-2 bg-[#EEF2FF]/50 text-xs font-manrope text-gray-700">
                  {r.error ? (
                    <p className="text-red-700">{r.error}</p>
                  ) : (
                    <>
                      <p><strong>Especificidade:</strong> {h.specificity_score ?? "—"} • <strong>Continuidade:</strong> {h.continuity_score ?? "—"} • <strong>Naturalidade:</strong> {h.naturalness_score ?? "—"}</p>
                      <p><strong>Comprimento:</strong> servida {s.text_length ?? "—"} vs humanizada {h.text_length ?? "—"} (diff {dv.length_diff ?? "—"})</p>
                      <p><strong>Perguntas:</strong> servida {s.question_count ?? "—"} vs humanizada {h.question_count ?? "—"} (diff {dv.question_count_diff ?? "—"})</p>
                      <p><strong>Segurança:</strong> violações da humanizada: {(h.supervisor_violations || []).join(", ") || "—"}</p>
                      {(h.violations || []).length > 0 && (
                        <p><strong>Violações (NaturalnessCritic):</strong> {h.violations.join(", ")}</p>
                      )}
                      <p><strong>Tokens:</strong> {h.input_tokens ?? "—"} in / {h.output_tokens ?? "—"} out • <strong>Latência:</strong> {h.latency_ms ?? "—"}ms (total {h.wall_latency_ms ?? "—"}ms)</p>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
